<template>
  <div>
    <FilterDrawer :show="showFilter" @update:show="$emit('update:showFilter', $event)">
      <KundenDublettenFilter
        @onClickSaveFilter="$emit('save-filter')"
        @onClickResetFilter="$emit('reset-filter')"
      ></KundenDublettenFilter>
    </FilterDrawer>
    <div class="d-flex flex-column flex-lg-row">
      <div v-if="kundenDuplicates" class="flex-lg-row-fluid">
        <div
          v-for="kundeDuplicate in kundenDuplicates"
          :key="kundeDuplicate.id"
          class="card card-custom w-100 gutter-b confirmation-card"
          :class="{
            'overlay overlay-block overlay-layer bg-dark-o-10': isBusy,
            hidden: kundeDuplicate.hidden,
          }"
        >
          <div class="card-header d-flex align-items-center justify-content-between">
            <span class="state-badge" :class="kundeDuplicate.state.toLowerCase()">
              {{ $t('DUBLETTEN.CONFIRMATION.' + kundeDuplicate.state.toUpperCase()) }}
            </span>
            <div class="d-flex align-items-center text-muted">
              <CalendarSync class="mr-2" :size="14" />
              <span class="mr-6">{{ kundeDuplicate.updatedDateUtc | dateTime }}</span>
              <FileCheck2 class="mr-2" :size="14" />
              <span class="mr-6">{{ kundeDuplicate.createdDateUtc | dateTime }}</span>
              <UserRoundPen class="mr-2" :size="14" />
              <span>{{ $store.getters.getUsernameById(kundeDuplicate.bearbeiter) || '-' }}</span>
            </div>
          </div>

          <div class="card-body">
            <KundeDublettenConfirmationCompareTable
              :kunde="kundeDuplicate.kunde"
              :duplicateKunde="kundeDuplicate.duplicateKunde"
              :mergePreview="kundeDuplicate.mergePreview"
              :state="kundeDuplicate.state"
            />
          </div>

          <div v-if="hasActions(kundeDuplicate.state)" class="card-footer d-flex justify-content-center">
            <b-button
              class="footer-button dialog-btn-secondary"
              v-if="kundeDuplicate.state === 'New'"
              variant="secondary"
              @click="onDeclineClick(kundeDuplicate)"
            >
              Ablehnen
            </b-button>
            <b-button
              class="footer-button dialog-btn"
              v-if="kundeDuplicate.state === 'New' || kundeDuplicate.state === 'Declined'"
              variant="primary"
              @click="onMergeClick(kundeDuplicate)"
            >
              {{ kundeDuplicate.state === 'New' ? 'Annehmen' : 'Korrektur: Annehmen' }}
            </b-button>
            <b-button
              class="footer-button dialog-btn"
              v-if="kundeDuplicate.state === 'Merged'"
              variant="warning"
              @click="onCardUnmergeClick(kundeDuplicate)"
            >
              Merge rückgängig machen
            </b-button>
          </div>
        </div>

        <div v-if="allItemsCount > top || allItemsCount === 0" class="card card-custom">
          <div class="card-body">
            <LoadSpinnerWidget
              v-if="allItemsCount === 0"
              :dataAvailable="allItemsCount !== 0"
              :isLoading="false"
              :showText="true"
            ></LoadSpinnerWidget>
            <SKRPagination
              v-if="allItemsCount > top"
              :overwriteCurrentPage="currentPage"
              :totalRows="allItemsCount"
              :isLoading="isBusy"
              :perPageOverwrite="top"
              :currentRows="kundenDuplicates.length"
              @pageChange="onPageChange"
            ></SKRPagination>
          </div>
        </div>
      </div>
      <div v-else class="w-100">
        <b-skeleton v-for="n in top" :key="n" class="gutter-b" width="100%" height="377px"></b-skeleton>
      </div>
    </div>
  </div>
</template>

<script>
import { SET_KUNDEN_CONFRIMATION_IS_LOADING } from '@/core/kunden/stores/kunden-dubletten-confirmation-filter.module';
import EventBus from '@/core/common/plugins/event-bus.js';

import * as odataService from '@/core/common/services/odata.service';
import apiService from '@/core/common/services/api.service';
import LoadSpinnerWidget from '@/components/common/load-spinner-widget.vue';
import SKRPagination from '@/components/common/skr-pagination.vue';

import KundeDublettenConfirmationCompareTable from '@/components/kunden/dubletten-confirmation-table-compare-table.vue';
import KundenDublettenFilter from '@/components/kunden/kunden-dubletten-filter.vue';
import FilterDrawer from '@/components/common/filter-drawer.vue';

import { CalendarSync, FileCheck2, UserRoundPen } from 'lucide-vue';

export default {
  components: {
    LoadSpinnerWidget,
    SKRPagination,
    KundeDublettenConfirmationCompareTable,
    KundenDublettenFilter,
    FilterDrawer,
    CalendarSync,
    FileCheck2,
    UserRoundPen,
  },
  props: {
    filter: { type: Array, required: true },
    appliedFilter: { type: Object, required: true },
    expand: { type: Object, required: true },
    showFilter: { type: Boolean, default: false },
  },
  data() {
    return {
      currentPage: null,
      allItemsCount: 0,
      kundenDuplicates: null,
      top: 10,
      skip: 0,
      isBusy: false,
    };
  },
  watch: {
    async appliedFilter() {
      this.skip = 0;
      this.currentPage = 1;
      this.refreshKunden();
    },
  },
  async mounted() {
    this.refreshKunden();

    EventBus.$on('reload', async () => {
      this.refreshKunden();
    });
  },
  beforeDestroy() {
    EventBus.$off('reload');
  },
  methods: {
    async sendTrigger(kundeId, duplicateKundeId, trigger) {
      const index = this.kundenDuplicates.findIndex(
        duplicate => duplicate.kundeId === kundeId && duplicate.duplicateKundeId === duplicateKundeId
      );

      await this.saveMergeStates(kundeId, duplicateKundeId, trigger);
      // Wait for animation (300ms) and then refresh
      this.$set(this.kundenDuplicates, index, {
        ...this.kundenDuplicates[index],
        hidden: true,
      });
      setTimeout(() => {
        this.refreshKunden();
      }, 400);
    },
    hasActions(state) {
      return state === 'New' || state === 'Declined' || state === 'Merged';
    },
    async onMergeClick(kundeDuplicate) {
      await this.sendTrigger(kundeDuplicate.kundeId, kundeDuplicate.duplicateKundeId, 0);
      this.toast('Kundensatz wurde zusammengeführt.', { variant: 'success' });
    },
    onDeclineClick(kundeDuplicate) {
      this.sendTrigger(kundeDuplicate.kundeId, kundeDuplicate.duplicateKundeId, 1);
    },
    onCardUnmergeClick(kundeDuplicate) {
      this.sendTrigger(kundeDuplicate.kundeId, kundeDuplicate.duplicateKundeId, 2);
    },
    async onPageChange(ctx) {
      this.currentPage = ctx.page;
      this.skip = ctx.skip;
      this.top = ctx.top;
      window.scrollTo(0, 0);
      await this.refreshKunden();
    },
    formatName(name) {
      const anrede = name.anrede === 'Frau' || name.anrede === 'Herr' ? name.anrede : '';
      return [anrede, name.titel, name.vorname, name.nachname].filter(Boolean).join(' ');
    },
    async refreshKunden() {
      this.kundenDuplicates = await this.getKundenData(this.skip, this.top);
    },
    parseKundenDuplicatesValuesAndAddDiff(kundenDuplicates) {
      return kundenDuplicates.map(kundenDuplicate => {
        const { kunde, duplicateKunde, resultCodes } = kundenDuplicate;
        if (!kunde || !duplicateKunde) return kundenDuplicate;

        if (kunde.name && duplicateKunde.name) {
          kunde.name = this.formatName(kunde.name);
          duplicateKunde.name = this.formatName(duplicateKunde.name);
        }

        kunde.adresse = this.parseAdresse(kunde.adresse);
        duplicateKunde.adresse = this.parseAdresse(duplicateKunde.adresse);

        kunde.nameMatch = resultCodes.includes('MS06');
        kunde.emailMatch = resultCodes.includes('MS07');
        kunde.adresseMatch = resultCodes.includes('MS06');
        kunde.telefonPrivatMatch = resultCodes.includes('MS09');
        kunde.telefonGeschaeftlichMatch = resultCodes.includes('MS10');
        kunde.telefonMobilMatch = resultCodes.includes('MS08');

        kundenDuplicate.mergePreview = this.parseMergePreview(kundenDuplicate.mergePreview);

        return { ...kundenDuplicate };
      });
    },
    parseMergePreview(mergePreview) {
      if (!mergePreview) return null;

      return {
        id: mergePreview.kundennummer,
        name: this.formatName(mergePreview),
        geburtsdatum: mergePreview.geburtsdatum,
        email: mergePreview.email,
        adresse: this.parseAdresse({
          strasse: mergePreview.strasse,
          postleitzahl: mergePreview.postleitzahl,
          ort: mergePreview.ort,
          land: mergePreview.land,
        }),
        telefonMobil: mergePreview.telefonMobil,
        telefonPrivat: mergePreview.telefonPrivat,
        telefonGeschaeftlich: mergePreview.telefonGeschaeftlich,
      };
    },
    parseAdresse(adresse) {
      return adresse
        ? `${adresse.strasse || ''}, ${adresse.postleitzahl || ''} ${adresse.ort || ''} ${adresse.land || ''}`
        : '';
    },
    async getKundenData(skip, top) {
      this.$store.commit(SET_KUNDEN_CONFRIMATION_IS_LOADING, true);
      this.isBusy = true;
      try {
        const response = await odataService.getKundeDuplicate({
          filter: this.filter,
          expand: this.expand,
          top: top,
          skip: skip,
          count: true,
          orderBy: 'updatedDateUtc desc',
        });
        this.allItemsCount = response.count;
        return this.parseKundenDuplicatesValuesAndAddDiff(response.data);
      } finally {
        this.isBusy = false;
        this.$store.commit(SET_KUNDEN_CONFRIMATION_IS_LOADING, false);
      }
    },
    async saveMergeStates(id, duplicateKundeId, trigger) {
      try {
        const data = await apiService.put(`Kunde/${id}/KundenDuplicates/${duplicateKundeId}`, {
          id: id,
          duplicateKundeId: duplicateKundeId,
          trigger: trigger,
        });
        return data;
      } finally {
        this.isBusy = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/sass/components/_variables.bootstrap.scss';

.confirmation-card > .card-header {
  min-height: 50px;
  padding: 0 24px;
}

.state-badge {
  padding: 10px 16px;
  border-radius: 5px;
  border-width: 1.5px;
  border-style: solid;
  font-weight: 700;
  font-size: 14px;
  line-height: 14px;
  color: #4b4b4b;

  &.new {
    border-color: var(--primary);
  }
  &.merged {
    border-color: #20c997;
  }
  &.declined {
    border-color: var(--danger);
  }
}

.card-body {
  background-color: var(--surface-hover);
}

.card-footer {
  height: 86px;
  gap: 8px;
  padding: 24px;
}

.footer-button {
  min-width: 116px;
  border-radius: 8px;
  align-self: center;
}

.confirmation-card {
  transition: 0.3s ease-out;
  opacity: 1;
  &.hidden {
    opacity: 0;
    left: 10px;
  }
}
</style>
